import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, Copy, Zap, Shield, Code, Package, Terminal, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import zipyLogo from "@/assets/zipy-logo.png";

const LOVABLE_PROMPT = `Integrate Zipy error monitoring into my Lovable project. Here's what I need:

1. **Zipy SDK** — Add the Zipy SDK script to index.html:
   \`\`\`html
   <script src="https://cdn.zipy.ai/sdk/v1.0/zipy.min.umd.js" crossorigin="anonymous"></script>
   <script>
     window.zipy && window.zipy.init('YOUR_ZIPY_API_KEY', {releaseVer: "1.0.0"});
   </script>
   \`\`\`

2. **Source Maps** — Enable source maps in vite.config.ts:
   \`\`\`ts
   build: { sourcemap: true }
   \`\`\`

3. **Source Map Upload** — Add zipy-cli to dependencies and configure postbuild in package.json:
   \`\`\`json
   "postbuild": "zipy-cli --authKey YOUR_ZIPY_AUTH_KEY --dirname ./dist --apiKey YOUR_ZIPY_API_KEY --releaseVer 1.0.0"
   \`\`\`
   Install zipy-cli: \`npm install zipy-cli\`

4. **User Identification** — In your auth/login flow, call:
   \`\`\`ts
   if (window.zipy) {
     window.zipy.identify(userEmail, { email: userEmail, name: userName });
   }
   \`\`\`

5. **TypeScript Support** — Add Zipy types to vite-env.d.ts:
   \`\`\`ts
   interface ZipySDK {
     init: (key: string) => void;
     identify: (uid: string, userInfo?: Record<string, string>) => void;
     anonymize: () => void;
   }
   interface Window { zipy?: ZipySDK; }
   \`\`\`

Replace YOUR_ZIPY_API_KEY and YOUR_ZIPY_AUTH_KEY with the keys from your Zipy dashboard.`;

const ZipySetup = () => {
  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [copiedOAuth, setCopiedOAuth] = useState(false);
  const { toast } = useToast();

  const copyToClipboard = (text: string, type: "prompt" | "oauth") => {
    navigator.clipboard.writeText(text);
    if (type === "prompt") {
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } else {
      setCopiedOAuth(true);
      setTimeout(() => setCopiedOAuth(false), 2000);
    }
    toast({ title: "Copied!", description: "Paste it into Lovable chat to get started.", duration: 3000 });
  };

  const oauthPrompt = `Connect Zipy to my Lovable project using OAuth/SSO. Use the same Google account I used to sign into Lovable to authenticate with Zipy, so I don't need to create a separate account. After authentication, automatically retrieve my Zipy API Key and Auth Key, then configure the SDK, source maps, and postbuild upload.`;

  const steps = [
    {
      number: "1",
      title: "Zipy SDK Loaded in index.html",
      description: "The Zipy SDK script is added to your HTML head, initializing with your API key and release version.",
      code: `<script src="https://cdn.zipy.ai/sdk/v1.0/zipy.min.umd.js"></script>\n<script>\n  window.zipy && window.zipy.init('YOUR_API_KEY', {releaseVer: "1.0.0"});\n</script>`,
    },
    {
      number: "2",
      title: "Source Maps Enabled in Vite",
      description: "Source maps are generated during build so Zipy can map minified errors back to your original source code.",
      code: `// vite.config.ts\nbuild: {\n  sourcemap: true\n}`,
    },
    {
      number: "3",
      title: "zipy-cli Configured for Postbuild Upload",
      description: "After every build, source maps are automatically uploaded to Zipy for readable stack traces in production.",
      code: `// package.json\n"postbuild": "zipy-cli --authKey AUTH_KEY --dirname ./dist --apiKey API_KEY --releaseVer 1.0.0"`,
    },
    {
      number: "4",
      title: "User Identification via zipy.identify()",
      description: "Logged-in users are identified in Zipy sessions, linking errors to specific users for better debugging.",
      code: `window.zipy?.identify(email, { email, source: 'app' });`,
    },
    {
      number: "5",
      title: "TypeScript Definitions Added",
      description: "Type-safe Zipy SDK interface in vite-env.d.ts prevents TypeScript errors and enables autocomplete.",
      code: `interface ZipySDK {\n  init: (key: string) => void;\n  identify: (uid: string, userInfo?: Record<string, string>) => void;\n}`,
    },
  ];

  return (
    <main className="min-h-screen">
      <ThemeToggle />
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Back to Home</Link>
        </div>
      </section>

      {/* Hero */}
      <section className="py-16 sm:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                One-Prompt Setup
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-foreground">
                Install Zipy in Lovable with One Prompt
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Copy a single prompt, paste it into Lovable chat, and get full Zipy error monitoring — SDK, source maps, postbuild upload, and user identification — all configured automatically.
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl overflow-hidden bg-background border border-border flex items-center justify-center p-8">
              <img src={zipyLogo} alt="Zipy Logo" className="w-48 h-48 object-contain" />
            </div>
          </div>
        </div>
      </section>

      {/* OAuth Flow (Assumed) */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30 text-accent text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Seamless Auth (Coming Soon)
            </div>
            <h2 className="font-heading text-3xl font-bold mb-4">OAuth/SSO Integration</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              When Zipy enables OAuth/SSO, Lovable will use your existing Google sign-in to authenticate with Zipy automatically — no separate signup needed.
            </p>
          </div>

          <Card className="p-6 border-2 border-accent/20 bg-accent/5">
            <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-accent" />
              Future OAuth Prompt
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              When available, paste this into Lovable to auto-authenticate and configure Zipy:
            </p>
            <div className="relative">
              <pre className="p-4 bg-background rounded-lg border border-border text-sm overflow-x-auto whitespace-pre-wrap font-mono text-foreground/80">
                {oauthPrompt}
              </pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-3 right-3 gap-1.5"
                onClick={() => copyToClipboard(oauthPrompt, "oauth")}
              >
                {copiedOAuth ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copiedOAuth ? "Copied" : "Copy"}
              </Button>
            </div>
            <div className="mt-4 p-3 rounded-lg bg-muted text-sm text-muted-foreground">
              <strong>How it would work:</strong> Lovable uses your Google session → Zipy OAuth validates → API Key & Auth Key are retrieved → SDK, source maps, and postbuild are auto-configured.
            </div>
          </Card>

          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[
              { icon: Shield, title: "Same Google ID", desc: "No new account — uses your Lovable Google sign-in" },
              { icon: Zap, title: "Auto-Config", desc: "API Key & Auth Key fetched and injected automatically" },
              { icon: Code, title: "Zero Manual Steps", desc: "SDK, source maps, postbuild — all done in one shot" },
            ].map((item, i) => (
              <Card key={i} className="p-4 text-center">
                <item.icon className="w-8 h-8 text-accent mx-auto mb-3" />
                <h4 className="font-heading font-semibold text-sm mb-1">{item.title}</h4>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Current Manual Setup — The One Prompt */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-bold mb-4">Available Now: One-Prompt Setup</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Copy this prompt, paste it into Lovable chat, replace the placeholder keys with your Zipy dashboard values, and everything gets configured.
            </p>
          </div>

          <Card className="p-6 border-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                <Package className="w-5 h-5 text-accent" />
                The Magic Prompt
              </h3>
              <Button
                size="sm"
                className="gap-1.5"
                onClick={() => copyToClipboard(LOVABLE_PROMPT, "prompt")}
              >
                {copiedPrompt ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedPrompt ? "Copied!" : "Copy Prompt"}
              </Button>
            </div>
            <pre className="p-4 bg-muted rounded-lg text-sm overflow-x-auto whitespace-pre-wrap font-mono text-foreground/80 max-h-[400px] overflow-y-auto">
              {LOVABLE_PROMPT}
            </pre>
          </Card>

          <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
            <h4 className="font-heading font-semibold mb-2 flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-accent" />
              Where to get your keys
            </h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
              <li>Go to <a href="https://app.zipy.ai" target="_blank" rel="noopener noreferrer" className="text-accent underline">app.zipy.ai</a> and sign in (or sign up)</li>
              <li>Navigate to <strong>Settings → Project Settings</strong> to find your <strong>API Key</strong></li>
              <li>Go to <strong>Settings → Source Maps</strong> to find your <strong>Auth Key</strong></li>
              <li>Replace <code className="px-1 py-0.5 bg-background rounded text-xs">YOUR_ZIPY_API_KEY</code> and <code className="px-1 py-0.5 bg-background rounded text-xs">YOUR_ZIPY_AUTH_KEY</code> in the prompt</li>
            </ol>
          </div>
        </div>
      </section>

      {/* What Gets Configured */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">What Gets Configured</h2>
          <div className="space-y-6">
            {steps.map((step) => (
              <Card key={step.number} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-accent">{step.number}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground mb-3">{step.description}</p>
                    <pre className="p-3 bg-muted rounded-lg text-xs font-mono overflow-x-auto text-foreground/70">
                      {step.code}
                    </pre>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Back to Zipy Integration */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-6">Want the Full Integration Guide?</h2>
          <p className="text-muted-foreground mb-8">See how Zipy connects to Healion for automatic bug detection and AI-powered fixes.</p>
          <Link to="/integrations/zipy">
            <Button size="lg" className="gap-2">
              View Zipy + Healion Integration <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ZipySetup;
